import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { GraduationCap, MessageCircle, Camera, BookOpen, Mic, Flame, Trophy, Sparkles, BadgeCheck, ArrowDown, Globe, Palette, Timer, Brain, Gamepad2, Map, PencilRuler, MessagesSquare, Repeat, Gift, ShieldCheck } from "lucide-react";
import OwlMascot from "@/components/OwlMascot";
import OwlLogo from "@/components/OwlLogo";
import { StoreButtons, QRBadge } from "@/components/StoreButtons";
import { PhoneMockup } from "@/components/PhoneMockup";
import { Reveal } from "@/components/Reveal";
import { useAppConfig } from "@/hooks/useAppConfig";

const LineReveal = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden pb-[0.1em] -mb-[0.1em] ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Scribble = () => (
  <svg className="absolute -bottom-3 left-0 w-full h-[0.22em]" viewBox="0 0 220 14" preserveAspectRatio="none" aria-hidden="true">
    <motion.path
      d="M4 10 C 60 2, 150 2, 216 8"
      fill="none"
      stroke="#F6BE3C"
      strokeWidth="7"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.8, delay: 1.2, ease: "easeOut" }}
    />
  </svg>
);

const MARQUEE_ITEMS = ["CP", "CE1", "CE2", "CM1", "CM2", "6e", "5e", "4e", "3e", "2de", "1re", "Terminale", "Mathématiques", "Français", "Anglais", "Espagnol", "Allemand", "Italien", "Histoire-Géographie", "Physique-Chimie", "SVT", "Technologie", "EMC", "Philosophie"];

const MarqueeRow = ({ hidden = false }) => (
  <div className="flex shrink-0" aria-hidden={hidden || undefined}>
    {MARQUEE_ITEMS.map((item, i) => (
      <span key={i} className="flex items-center gap-8 pr-8 shrink-0">
        <span className="font-serif italic text-2xl md:text-3xl text-ink/85 whitespace-nowrap">{item}</span>
        <Sparkles className="w-4 h-4 text-coral shrink-0" />
      </span>
    ))}
  </div>
);

const NIVEAUX = [
  {
    cycle: "Primaire",
    grades: "CP · CE1 · CE2 · CM1 · CM2",
    desc: "Lire, écrire, compter : le compagnon rend les bases solides et rigolotes, avec des leçons adaptées au programme de l'Éducation nationale.",
    img: "https://images.unsplash.com/photo-1542307125-d50bf9899025?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxmcmVuY2glMjBzY2hvb2wlMjBjaGlsZCUyMGxlYXJuaW5nJTIwdGFibGV0JTIwaG9tZXdvcmt8ZW58MHx8fHwxNzkwMjgwNjE4fDA&ixlib=rb-4.1.0&q=85",
    alt: "Élève de primaire faisant ses devoirs sur tablette",
  },
  {
    cycle: "Collège",
    grades: "6e · 5e · 4e · 3e",
    desc: "Méthode, organisation et reprise des chapitres : le compagnon aide à comprendre avant de mémoriser, brevet inclus.",
    img: "https://images.unsplash.com/photo-1660605042483-0fc1882cfba9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxmcmVuY2glMjBzY2hvb2wlMjBjaGlsZCUyMGxlYXJuaW5nJTIwdGFibGV0JTIwaG9tZXdvcmt8ZW58MHx8fHwxNzkwMjgwNjE4fDA&ixlib=rb-4.1.0&q=85",
    alt: "Élève écrivant dans son cahier",
  },
  {
    cycle: "Lycée",
    grades: "2de · 1re · Terminale",
    desc: "Dissertations, exercices complexes, préparation du bac : un accompagnement exigeant, matière par matière.",
    img: "https://images.pexels.com/photos/9490503/pexels-photo-9490503.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Lycéen étudiant avec une tablette",
  },
];

const FEATURES = [
  { icon: MessageCircle, title: "Dialogue socratique", text: "Dans toutes les matières du programme : maths, français, langues — l'italien dès la 5e —, sciences, EMC et philosophie en Terminale." },
  { icon: Brain, title: "Profil d'apprentissage", text: "Les explications s'ajustent à sa façon d'apprendre : visuel, auditif, rythme, répétition. Détection discrète, adaptation automatique." },
  { icon: Camera, title: "Aide en photo", text: "Photographie ton exercice, le compagnon t'aide à le faire toi-même — sans jamais faire à ta place." },
  { icon: Mic, title: "Apprendre à l'oral", text: "Cours lus à voix haute, dictées vocales, débats d'EMC au micro et traducteur audio." },
];

const ACTIVITIES = [
  { icon: BookOpen, title: "Leçons, interros, exercices, fiches", text: "Tout le contenu par chapitre et par classe, pour chaque matière du programme.", cls: "bg-chalk text-paper", iconCls: "bg-paper/10 text-hi", span: "lg:col-span-2" },
  { icon: Gamepad2, title: "Jeux interactifs", text: "Souligner, relier, classer : réviser en jouant.", cls: "bg-hi text-ink", iconCls: "bg-ink text-hi", span: "" },
  { icon: Map, title: "Schémas à légender", text: "Cellule, cœur, volcan, carte de France, frise chronologique, circuit électrique, engrenages… on glisse les mots au bon endroit.", cls: "bg-white text-ink", iconCls: "bg-hi text-ink", span: "" },
  { icon: PencilRuler, title: "Figures à tracer au doigt", text: "Hauteur, médiane, rayon, coordonnées et dessin libre corrigé : vérifiés instantanément.", cls: "bg-white text-ink", iconCls: "bg-mint text-ink", span: "" },
  { icon: Mic, title: "Dictées vocales", text: "Dictées par le compagnon, correction mot par mot et règles expliquées simplement.", cls: "bg-mint text-ink", iconCls: "bg-ink text-mint", span: "" },
  { icon: Timer, title: "Fluence de lecture", text: "Lecture chronométrée d'une minute, avec suivi des progrès.", cls: "bg-white text-ink", iconCls: "bg-coral text-ink", span: "" },
  { icon: MessagesSquare, title: "Mini-débats d'EMC", text: "Le compagnon défend une idée, à l'enfant d'argumenter.", cls: "bg-white text-ink", iconCls: "bg-hi text-ink", span: "" },
  { icon: Repeat, title: "Rappels espacés", text: "Les notions fragiles reviennent au bon moment pour ne rien oublier.", cls: "bg-white text-ink", iconCls: "bg-ink text-paper", span: "lg:col-span-2" },
];

const GALLERY = [
  { src: "/store-kit/captures-ecran/01-accueil.png", label: "Accueil" },
  { src: "/store-kit/captures-ecran/02-matiere.png", label: "Matières & chapitres" },
  { src: "/store-kit/captures-ecran/03-progres.png", label: "Mes progrès" },
  { src: "/store-kit/captures-ecran/04-schemas.png", label: "Schémas" },
  { src: "/store-kit/captures-ecran/05-messages.png", label: "Messages" },
  { src: "/store-kit/captures-ecran/06-parents.png", label: "Espace parents" },
];

const Hero = ({ config }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yChips = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yOwl = useTransform(scrollYProgress, [0, 1], [0, 50]);

  const chips = [
    { label: "CP", cls: "left-2 lg:-left-6 top-8 -rotate-6", delay: 0 },
    { label: "5e · Maths ✓", cls: "right-0 lg:-right-2 top-24 rotate-3", delay: 0.2 },
    { label: "1re · Histoire-Géo", cls: "left-6 bottom-24 rotate-2", delay: 0.35 },
    { label: "Tle", cls: "right-4 bottom-10 -rotate-3", delay: 0.5 },
  ];

  return (
    <section ref={ref} className="relative overflow-hidden bg-grid-paper pt-28 lg:pt-36 pb-16 lg:pb-24" data-testid="hero-section">
      <div className="absolute -right-48 -top-48 w-[620px] h-[620px] rounded-full bg-coral/15 blur-3xl" aria-hidden="true" />
      <div className="absolute -left-40 bottom-0 w-[420px] h-[420px] rounded-full bg-hi/25 blur-3xl" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center relative">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white/70 px-4 py-1.5 text-[11px] font-mono font-semibold uppercase tracking-[0.18em] text-ink/70">
              <GraduationCap className="w-3.5 h-3.5 text-coral" />
              Le tuteur qui fait réfléchir · CP → Terminale
            </span>
          </motion.div>
          <h1
            className="mt-6 font-serif font-bold tracking-tight leading-[1.02] text-[clamp(2.7rem,7.5vw,5.6rem)]"
            data-testid="hero-title"
          >
            <LineReveal delay={0.1}>Ici, on ne donne</LineReveal>
            <LineReveal delay={0.24}>jamais la réponse.</LineReveal>
            <LineReveal delay={0.38}>
              <span className="italic text-coral relative inline-block pr-2">
                On la fait trouver.
                <Scribble />
              </span>
            </LineReveal>
          </h1>
          <motion.p
            className="mt-7 max-w-xl text-lg sm:text-xl text-ink/70 leading-relaxed"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            data-testid="hero-subtitle"
          >
            Le tuteur bienveillant qui aide votre enfant à apprendre… en réfléchissant par lui-même. Le compagnon animé pose les bonnes questions, repère les lacunes et guide l'élève jusqu'au « eurêka ».
          </motion.p>
          <motion.div
            className="mt-9 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
          >
            <StoreButtons appStoreUrl={config.app_store_url} playStoreUrl={config.play_store_url} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-6"
          >
            <QRBadge value={config.site_url} />
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative px-6 sm:px-16 lg:px-0">
          <motion.div style={{ y: yOwl }}>
            <OwlMascot className="w-full max-w-[420px] mx-auto" />
          </motion.div>
          {chips.map((chip, i) => (
            <motion.span
              key={i}
              style={{ y: yChips }}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 1 + i * 0.15, ease: "backOut" }}
              className={`absolute ${chip.cls} rounded-xl bg-white border border-ink/10 px-4 py-2 shadow-xl shadow-ink/10 text-sm font-bold`}
              data-testid={`hero-chip-${i}`}
            >
              {chip.label}
            </motion.span>
          ))}
        </div>
      </div>
      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-ink/40 hidden lg:block"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.2, repeat: Infinity }}
      >
        <ArrowDown className="w-5 h-5" />
      </motion.div>
    </section>
  );
};

const CompanionSection = ({ config }) => (
  <section id="app" className="py-24 lg:py-32 scroll-mt-16" data-testid="companion-section">
    <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
      <Reveal className="relative">
        <PhoneMockup companionName={config.companion_name} welcomeMessage={config.welcome_message} />
      </Reveal>
      <div>
        <Reveal><span className="eyebrow">Le compagnon virtuel</span></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" data-testid="companion-title">
            Un compagnon qui s'adapte <span className="italic text-coral">à chaque enfant</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-lg text-ink/70 leading-relaxed">
            Plutôt que de donner la réponse, ton compagnon animé te pose les bonnes questions pour que tu comprennes par toi-même. Tu progresses vraiment, étape par étape.
          </p>
        </Reveal>
        <div className="mt-9 grid sm:grid-cols-2 gap-5">
          {FEATURES.map((f, i) => (
            <Reveal key={i} delay={0.1 + i * 0.06} className="h-full">
              <div className="flex gap-3.5 items-start group h-full">
                <div className="w-11 h-11 rounded-xl bg-hi text-ink flex items-center justify-center shrink-0 transition-transform group-hover:-rotate-6">
                  <f.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-sm">{f.title}</p>
                  <p className="text-sm text-ink/60 mt-0.5 leading-relaxed">{f.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ActivitiesSection = () => (
  <section id="activites" className="py-24 lg:py-32 bg-paper2/50 border-y border-ink/5 scroll-mt-16" data-testid="activities-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="max-w-2xl">
        <Reveal><span className="eyebrow">Les activités</span></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" data-testid="activities-title">
            Des activités pour <span className="italic text-coral">toutes les mémoires</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-lg text-ink/70 leading-relaxed">
            Leçons, jeux, dictées, débats, tracés : chaque notion revient sous plusieurs formes, pour entrer dans la tête de chaque enfant.
          </p>
        </Reveal>
      </div>
      <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {ACTIVITIES.map((a, i) => (
          <Reveal key={i} delay={i * 0.05} className={`h-full ${a.span}`}>
            <div className={`rounded-3xl p-7 h-full flex flex-col border border-ink/10 ${a.cls} transition-transform hover:-translate-y-1`} data-testid={`activity-card-${i}`}>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${a.iconCls}`}>
                <a.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-5 font-bold text-lg leading-snug">{a.title}</h3>
              <p className="mt-2 text-sm leading-relaxed opacity-75">{a.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const NiveauxSection = () => (
  <section id="niveaux" className="py-24 lg:py-32 scroll-mt-16" data-testid="niveaux-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="max-w-2xl">
        <Reveal><span className="eyebrow">Le parcours</span></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" data-testid="niveaux-title">
            Un compagnon qui grandit <span className="italic text-coral">avec eux</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-lg text-ink/70 leading-relaxed">
            Votre enfant indique son âge : seules les classes proches de son niveau lui sont proposées, du CP à la Terminale, conformément au programme de l'Éducation nationale.
          </p>
        </Reveal>
      </div>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {NIVEAUX.map((n, i) => (
          <Reveal key={n.cycle} delay={i * 0.1} className="h-full">
            <article className="card-paper h-full flex flex-col overflow-hidden group" data-testid={`niveau-card-${i}`}>
              <div className="h-52 overflow-hidden">
                <img
                  src={n.img}
                  alt={n.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  style={{ filter: "saturate(0.92) contrast(1.05)" }}
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-serif text-2xl font-bold" data-testid={`niveau-title-${i}`}>{n.cycle}</h3>
                <p className="mt-1 text-xs font-mono font-semibold uppercase tracking-[0.15em] text-coral">{n.grades}</p>
                <p className="mt-3 text-sm text-ink/65 leading-relaxed flex-1">{n.desc}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const MotivationSection = () => (
  <section id="motivation" className="py-24 lg:py-32 scroll-mt-16" data-testid="motivation-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="max-w-2xl">
        <Reveal><span className="eyebrow">La motivation en plus</span></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" data-testid="motivation-title">
            Des révisions qui donnent <span className="italic text-coral">envie de revenir</span>
          </h2>
        </Reveal>
      </div>
      <div className="mt-12 grid gap-6 lg:grid-cols-3">
        <Reveal className="lg:col-span-2 h-full" delay={0.05}>
          <div className="rounded-3xl bg-hi text-ink p-8 lg:p-10 h-full flex flex-col sm:flex-row gap-8 items-center overflow-hidden relative" data-testid="motivation-avatar-card">
            <div className="flex-1">
              <div className="w-11 h-11 rounded-xl bg-ink text-hi flex items-center justify-center">
                <Palette className="w-5 h-5" />
              </div>
              <h3 className="mt-5 font-serif text-2xl lg:text-3xl font-bold leading-tight">Des pièces, une boutique, des compagnons animés</h3>
              <p className="mt-3 text-sm lg:text-base leading-relaxed text-ink/80">
                En révisant, ton enfant gagne des pièces à dépenser dans la boutique pour personnaliser son avatar — et débloquer des compagnons animés : chouette, renard, chat…
              </p>
            </div>
            <div className="flex flex-col items-center gap-4 shrink-0">
              <div className="w-28 h-28 rounded-full bg-white ring-4 ring-mint flex items-center justify-center shadow-lg">
                <OwlLogo variant="face" className="w-16 h-16" />
              </div>
              <div className="flex gap-1.5">
                {["#F2549B", "#7FD6C2", "#F6BE3C", "#8A5A2E"].map((c) => (
                  <span key={c} className="w-5 h-5 rounded-full border-2 border-ink/80" style={{ background: c }} />
                ))}
              </div>
            </div>
          </div>
        </Reveal>
        <Reveal className="h-full" delay={0.12}>
          <div className="rounded-3xl bg-mint text-ink p-8 h-full flex flex-col" data-testid="motivation-streaks-card">
            <div className="w-11 h-11 rounded-xl bg-ink text-mint flex items-center justify-center">
              <Flame className="w-5 h-5" />
            </div>
            <h3 className="mt-5 font-serif text-2xl font-bold leading-tight">Badges, séries et objectifs</h3>
            <p className="mt-3 text-sm leading-relaxed text-ink/80 flex-1">
              Des séries de jours, des badges à collectionner et des objectifs de la semaine pour garder le cap, sans pression.
            </p>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-3 h-full" delay={0.18}>
          <div className="rounded-3xl bg-coral text-ink p-8 lg:p-10 h-full flex flex-col sm:flex-row sm:items-center gap-6" data-testid="motivation-challenges-card">
            <div className="flex gap-3.5 shrink-0 items-center">
              <div className="w-11 h-11 rounded-xl bg-ink text-coral flex items-center justify-center">
                <Trophy className="w-5 h-5" />
              </div>
              <div className="w-11 h-11 rounded-xl bg-ink text-coral flex items-center justify-center">
                <Timer className="w-5 h-5" />
              </div>
            </div>
            <div>
              <h3 className="font-serif text-2xl lg:text-3xl font-bold leading-tight">Défis en direct entre amis</h3>
              <p className="mt-2 text-sm lg:text-base leading-relaxed text-ink/80">
                Façon quiz multijoueur : lance un défi et comparez vos scores, avec chrono partagé en direct.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const GallerySection = () => (
  <section id="apercu" className="py-24 lg:py-32 bg-paper2/50 border-y border-ink/5 scroll-mt-16" data-testid="gallery-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="max-w-2xl">
        <Reveal><span className="eyebrow">En images</span></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" data-testid="gallery-title">
            L'application <span className="italic text-coral">en vrai</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-lg text-ink/70 leading-relaxed">
            Six écrans, une promesse : comprendre vraiment — et réviser en toute sécurité.
          </p>
        </Reveal>
      </div>
      <div className="mt-12 flex gap-6 overflow-x-auto pb-6 -mx-6 px-6 lg:mx-0 lg:px-0 lg:grid lg:grid-cols-6 lg:overflow-visible">
        {GALLERY.map((g, i) => (
          <Reveal key={i} delay={i * 0.06} className="shrink-0 w-[200px] sm:w-[230px] lg:w-auto lg:min-w-0">
            <div className="rounded-[1.7rem] bg-ink p-1.5 shadow-xl shadow-ink/15 transition-transform hover:-translate-y-1.5" data-testid={`gallery-phone-${i}`}>
              <img
                src={g.src}
                alt={`Écran ${g.label} de l'application Chouette Apprentissage`}
                loading="lazy"
                className="rounded-[1.4rem] w-full object-cover"
                style={{ aspectRatio: "1290/2796" }}
              />
            </div>
            <p className="mt-3 text-center text-xs font-bold text-ink/60">{g.label}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ParentsSection = ({ config }) => (
  <section id="parents" className="py-24 lg:py-32 scroll-mt-16" data-testid="parents-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="rounded-[2.5rem] bg-chalk text-paper px-6 sm:px-12 lg:px-16 py-14 lg:py-20 grid lg:grid-cols-2 gap-14 items-center overflow-hidden relative">
        <div className="absolute -right-24 -top-24 w-[380px] h-[380px] rounded-full bg-chalk2/60 blur-3xl" aria-hidden="true" />
        <div className="relative">
          <Reveal><span className="eyebrow text-paper/60">Pour les représentants légaux</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" data-testid="parents-title">
              Un espace parents <span className="italic text-hi">complet et sécurisé</span>
            </h2>
          </Reveal>
          <ul className="mt-8 space-y-4">
            {[
              "Suivi : progrès par matière, chapitres acquis, profil d'apprentissage, moyenne aux interros",
              "Compte famille : jusqu'à 2 représentants par enfant (co-parents, famille recomposée), invitation par e-mail",
              "Temps d'écran : plage horaire (semaine / week-end) ou durée quotidienne, bonus 15 min d'un tap",
              "Messagerie protégée : amis approuvés par un parent, chaque message analysé par un gardien IA, échanges privés entre co-parents avec accusé de lecture",
              "Aucune publicité, aucune revente de données, données hébergées en France",
            ].map((item, i) => (
              <Reveal key={i} delay={0.1 + i * 0.07}>
                <li className="flex items-start gap-3 text-paper/90" data-testid={`parents-point-${i}`}>
                  <BadgeCheck className="w-5 h-5 text-hi shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={0.4}>
            <p className="mt-5 text-xs text-paper/55 leading-relaxed" data-testid="parents-social-note">
              Sécurité renforcée : e-mail confirmé et identité vérifiée (pièce d'identité + selfie) avant toute fonction sociale.
            </p>
          </Reveal>
          <Reveal delay={0.45}>
            <Link
              to="/confidentialite"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-paper text-ink px-6 py-3 text-sm font-bold transition-all hover:bg-hi hover:-translate-y-0.5"
              data-testid="parents-privacy-cta"
            >
              <ShieldCheck className="w-4 h-4" />
              Lire notre politique de confidentialité
            </Link>
          </Reveal>
        </div>
        <Reveal className="relative">
          <div className="relative">
            <div className="absolute -inset-3 rounded-[2rem] border-2 border-hi/50 rotate-2" aria-hidden="true" />
            <img
              src="/store-kit/captures-ecran/06-parents.png"
              alt="Écran de l'espace parents de l'application Chouette Apprentissage"
              loading="lazy"
              className="relative rounded-[1.8rem] w-full max-w-[380px] mx-auto object-cover shadow-2xl shadow-ink/30"
              style={{ aspectRatio: "1290/2796" }}
              data-testid="parents-photo"
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const DownloadSection = ({ config }) => (
  <section id="telecharger" className="pb-28 lg:pb-36 scroll-mt-16" data-testid="download-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="rounded-[2.5rem] bg-grid-paper border border-ink/10 px-6 sm:px-12 py-16 lg:py-24 text-center relative overflow-hidden">
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-40 w-[700px] h-[400px] rounded-full bg-coral/15 blur-3xl" aria-hidden="true" />
        <Reveal>
          <img
            src="/store-kit/icone-512.png"
            alt="Icône de l'application Chouette Apprentissage"
            className="w-20 rounded-2xl shadow-lg mx-auto border border-ink/10"
            data-testid="download-app-icon"
          />
        </Reveal>
        <Reveal delay={0.05}>
          <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-hi text-ink px-4 py-1.5 text-xs font-bold uppercase tracking-wide" data-testid="download-trial-pill">
            <Gift className="w-4 h-4" />
            14 jours d'essai gratuit
          </span>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-serif text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="download-title">
            Transformez les révisions<br />en aventure
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 max-w-xl mx-auto w-full text-left">
            <div className="card-paper p-6" data-testid="pricing-solo">
              <div className="flex items-center justify-between gap-2">
                <p className="font-bold">Solo</p>
                <span className="rounded-full bg-paper2 px-3 py-1 text-[10px] font-mono font-bold uppercase tracking-wider">1 enfant</span>
              </div>
              <p className="mt-2 font-serif text-3xl font-bold">3,00 €<span className="ml-1 text-sm font-sans font-semibold text-ink/50">/mois</span></p>
            </div>
            <div className="card-paper p-6" data-testid="pricing-famille">
              <div className="flex items-center justify-between gap-2">
                <p className="font-bold">Pack Famille</p>
                <span className="rounded-full bg-mint px-3 py-1 text-[10px] font-mono font-bold uppercase tracking-wider">2 enfants et +</span>
              </div>
              <p className="mt-2 font-serif text-3xl font-bold">5,00 €<span className="ml-1 text-sm font-sans font-semibold text-ink/50">/mois</span></p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-9 flex flex-col items-center gap-7">
            <StoreButtons appStoreUrl={config.app_store_url} playStoreUrl={config.play_store_url} />
            <QRBadge value={config.site_url} />
            <p className="inline-flex items-center gap-2 text-sm font-semibold text-ink/60" data-testid="download-domain-pill">
              <Globe className="w-4 h-4 text-coral" />
              chouetteapprentissage.com
            </p>
            <div className="space-y-2">
              <p className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2 text-xs font-bold" data-testid="download-ptit-services">
                <BadgeCheck className="w-4 h-4 text-coral" />
                Abonnement gratuit pour les clients Ptit Services
              </p>
              <p className="text-xs text-ink/45 max-w-md mx-auto leading-relaxed">
                Paiement géré par le représentant légal via l'App Store ou Google Play. Renouvellement automatique, résiliable à tout moment depuis votre compte.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  const config = useAppConfig();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 500);
      return () => clearTimeout(t);
    }
  }, [location]);

  return (
    <main>
      <Hero config={config} />
      <div className="border-y border-ink/10 bg-paper2/60 py-5 overflow-hidden" data-testid="marquee-band">
        <div className="flex w-max animate-marquee hover:[animation-play-state:paused]">
          <MarqueeRow />
          <MarqueeRow hidden />
        </div>
      </div>
      <CompanionSection config={config} />
      <ActivitiesSection />
      <NiveauxSection />
      <MotivationSection />
      <GallerySection />
      <ParentsSection config={config} />
      <DownloadSection config={config} />
    </main>
  );
}